import * as React from 'react';
import { useQuery } from 'react-query';
import {
  CheckCircle2,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  FilePlus2,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { useFetchViagensControle } from 'hooks/api/viagens-controle';
import { cn } from 'lib/utils';
import type { ViagensControleFilters } from '../types';
import { formatViagemCurrency } from '../utils';

type IndicatorCardProps = {
  title: string;
  value: number | null | undefined;
  quantity: number;
  icon: React.ReactNode;
  active?: boolean;
  loading?: boolean;
  onClick?: () => void;
};

const IndicatorCard = ({
  title,
  value,
  quantity,
  icon,
  active,
  loading,
  onClick,
}: IndicatorCardProps) => {
  const content = (
    <Card
      className={cn(
        'h-full min-h-[130px] min-w-0 border-2 border-transparent transition',
        active && 'border-orange-500'
      )}
    >
      <CardHeader className="p-4 pb-3 2xl:p-6 2xl:pb-3">
        <div className="flex w-full min-w-0 items-start justify-between gap-2">
          <h5 className="min-w-0 pr-1 font-GilroySemibold text-sm leading-tight text-slate-800">
            {title}
          </h5>
          <div className="flex w-6 shrink-0 justify-center">{icon}</div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 2xl:p-6 2xl:pt-0">
        <div className="flex min-w-0 items-center justify-between gap-2 2xl:gap-4">
          <div className="min-w-0 flex-1">
            <div className="whitespace-nowrap font-GilroyBold text-2xl leading-none text-orange-500 2xl:text-3xl">
              {formatViagemCurrency(value)}
            </div>
          </div>
          <div className="flex min-w-[3.5rem] shrink-0 justify-end border-l border-slate-200 pl-2 2xl:min-w-[4.5rem] 2xl:pl-4">
            <div className="flex w-6 justify-center font-GilroyBold text-lg leading-none text-slate-700 2xl:text-xl">
              {quantity}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (!onClick) {
    return (
      <div className={cn('block w-full text-left', loading && 'opacity-30')}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={cn(
        'block w-full text-left transition disabled:cursor-wait',
        loading && 'opacity-30'
      )}
    >
      {content}
    </button>
  );
};

export const ViagensControleIndicators = ({
  filters,
  onSubmitFilters,
}: {
  filters: ViagensControleFilters;
  onSubmitFilters: (filters: ViagensControleFilters) => void;
}) => {
  const { fetchViagensControleIndicators } = useFetchViagensControle();
  const indicatorFilters = React.useMemo(() => {
    const { approvalStatus, indicatorStatus, ...rest } = filters;
    return rest;
  }, [filters]);

  const indicatorsQuery = useQuery({
    queryKey: ['fetch-viagens-controle-indicators', indicatorFilters],
    queryFn: () => fetchViagensControleIndicators(indicatorFilters),
    refetchOnWindowFocus: false,
  });

  const indicator = indicatorsQuery.data;
  const loading = indicatorsQuery.isLoading;

  const handleToggleIndicator = React.useCallback(
    (indicatorStatus: NonNullable<ViagensControleFilters['indicatorStatus']>) => {
      onSubmitFilters({
        ...filters,
        approvalStatus: undefined,
        indicatorStatus:
          filters.indicatorStatus === indicatorStatus ? undefined : indicatorStatus,
      });
    },
    [filters, onSubmitFilters]
  );

  const handleClearIndicator = React.useCallback(() => {
    onSubmitFilters({
      ...filters,
      approvalStatus: undefined,
      indicatorStatus: undefined,
    });
  }, [filters, onSubmitFilters]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3 2xl:grid-cols-6 2xl:gap-6">
      <IndicatorCard
        title="Valor Total de Estadias"
        value={indicator?.total.value}
        quantity={indicator?.total.quantity ?? 0}
        icon={<CircleDollarSign className="stroke-slate-800" />}
        loading={loading}
        onClick={handleClearIndicator}
      />
      <IndicatorCard
        title="Aguardando Validação"
        value={indicator?.awaitingValidation.value}
        quantity={indicator?.awaitingValidation.quantity ?? 0}
        icon={<ClipboardCheck className="stroke-slate-800" />}
        loading={loading}
        active={filters.indicatorStatus === 'awaitingValidation'}
        onClick={() => handleToggleIndicator('awaitingValidation')}
      />
      <IndicatorCard
        title="Aguardando Gerar Fatura"
        value={indicator?.awaitingBilling.value}
        quantity={indicator?.awaitingBilling.quantity ?? 0}
        icon={<FilePlus2 className="stroke-slate-800" />}
        loading={loading}
        active={filters.indicatorStatus === 'awaitingBilling'}
        onClick={() => handleToggleIndicator('awaitingBilling')}
      />
      <IndicatorCard
        title="Aguardando Aprovação"
        value={indicator?.awaitingApproval.value}
        quantity={indicator?.awaitingApproval.quantity ?? 0}
        icon={<Clock3 className="stroke-slate-800" />}
        loading={loading}
        active={filters.indicatorStatus === 'awaitingApproval'}
        onClick={() => handleToggleIndicator('awaitingApproval')}
      />
      <IndicatorCard
        title="Aprovados"
        value={indicator?.approved.value}
        quantity={indicator?.approved.quantity ?? 0}
        icon={<CheckCircle2 className="stroke-slate-800" />}
        loading={loading}
        active={filters.indicatorStatus === 'approved'}
        onClick={() => handleToggleIndicator('approved')}
      />
      <IndicatorCard
        title="Reprovados"
        value={indicator?.rejected.value}
        quantity={indicator?.rejected.quantity ?? 0}
        icon={<XCircle className="stroke-slate-800" />}
        loading={loading}
        active={filters.indicatorStatus === 'rejected'}
        onClick={() => handleToggleIndicator('rejected')}
      />
    </div>
  );
};
